import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import { Tabs as FlowbiteTabs } from 'flowbite-react';
import { useNavigate, useLocation } from 'react-router-dom';
import './tabs.css';

const Tabs = forwardRef(({
    children,
    className = '',
    variant = 'underline',
    ariaLabel = 'Tabs',
    onTabChange,
    ...rest
}, ref) => {
    const navigate = useNavigate();
    const location = useLocation();
    const tabsRef = useRef(null);
    
    useImperativeHandle(ref, () => ({
        setActiveTab: (activeTab) => {
            tabsRef.current?.setActiveTab(activeTab);
        }
    }));
    
    const childrenArray = React.Children.toArray(children);
    
    const isNavigationMode = childrenArray.some(child => 
        React.isValidElement(child) && child.props.to
    );
    
    const getActiveTabFromUrl = () => {
        const currentPath = location.pathname;
        const matchedIndex = childrenArray.findIndex(child => 
            React.isValidElement(child) && child.props.to === currentPath
        );
        return matchedIndex >= 0 ? matchedIndex : 0;
    };
    
    const currentActiveTab = isNavigationMode ? getActiveTabFromUrl() : undefined;
    
    const handleTabChange = (tabIndex) => {
        const child = childrenArray[tabIndex];
        
        if (React.isValidElement(child)) {
            if (child.props.to) {
                navigate(child.props.to);
                return;
            }
            
            if (onTabChange) {
                onTabChange(tabIndex);
            }
        }
    };
    
    const processedChildren = isNavigationMode 
        ? React.Children.map(children, (child, index) => {
            if (React.isValidElement(child)) {
                const isActive = index === currentActiveTab;
                return React.cloneElement(child, {
                    ...child.props,
                    active: isActive
                });
            }
            return child;
        })
        : children;
    
    return (
        <FlowbiteTabs
            ref={tabsRef}
            aria-label={ariaLabel}
            variant={variant}
            className={`ui-tabs border-none ${className}`}
            onActiveTabChange={handleTabChange}
            {...rest}
        >
            {processedChildren}
        </FlowbiteTabs>
    );
});

// Re-export Tabs.Item for convenience
Tabs.Item = FlowbiteTabs.Item;

export default Tabs;